import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

interface CoachProfile {
  id: number;
  bio: string;
  specialties: string;
  sport: string;
  hourly_rate: string;
  address?: string;
  payment_methods?: string;
  education?: string;
  diplomas?: string;
  experience?: string;
  languages?: string;
  photo?: string;
  iban?: string;
  bic?: string;
  city?: string;
  postal_code?: string;
  department?: string;
  training_city?: string;
  is_itinerant?: boolean;
  social_instagram?: string;
  social_linkedin?: string;
  social_facebook?: string;
  social_website?: string;
  social_tiktok?: string;
  social_x?: string;
  social_snapchat?: string;
  social_youtube?: string;
  social_pinterest?: string;
  social_whatsapp?: string;
}

interface SportifProfile {
  id: number;
  sport: string;
  height?: string;
  weight?: string;
  goals?: string;
  medical_info?: string;
  photo?: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  role: 'COACH' | 'SPORTIF' | 'ADMIN';
  is_demo?: boolean;
  coach_profile?: CoachProfile;
  sportif_profile?: SportifProfile;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isDemo: boolean;
  login: (token: string, refresh: string) => Promise<User | null>;
  demoLogin: (role: 'COACH' | 'SPORTIF') => Promise<User | null>;
  logout: (redirectTo?: string) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const response = await api.get('/users/me/');
        setUser(response.data);
      } catch (error: any) {
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('sportif_tab');
          localStorage.removeItem('coach_tab');
          setUser(null);
        } else {
          console.error("Auth check failed", error);
        }
      }
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      await refreshUser();
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (token: string, refresh: string): Promise<User | null> => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('refresh_token', refresh);
    try {
      const response = await api.get('/users/me/');
      setUser(response.data);
      return response.data;
    } catch {
      return null;
    }
  };

  /**
   * Ouvre une session sur un compte de demonstration, sans inscription.
   * Le backend ne delivre ces jetons que via cet endpoint : les comptes de
   * demo ont un mot de passe inutilisable.
   */
  const demoLogin = async (role: 'COACH' | 'SPORTIF'): Promise<User | null> => {
    try {
      const { data } = await api.post('/users/demo-login/', { role });
      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);
      setUser(data.user);
      return data.user;
    } catch {
      return null;
    }
  };

  const logout = (redirectTo?: unknown) => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('sportif_tab');
    localStorage.removeItem('coach_tab');
    // redirectTo n'est retenu que si c'est bien une chaine. Plusieurs boutons
    // appellent onClick={onLogout}, ce qui transmet l'evenement du clic comme
    // premier argument ; sans ce garde, window.location.href recevait un objet
    // et naviguait vers "/coach/[object Object]".
    // Apres deconnexion on renvoie vers la landing page (accueil public), pas
    // vers l'ecran de connexion.
    const cible = typeof redirectTo === 'string' ? redirectTo : '/';
    // Pas de setUser(null) : cela reprovoquerait un rendu du tableau de bord
    // courant avec user=null (CoachDashboard lit user.coach_profile sans garde).
    // La redirection recharge toute la page et reinitialise l'etat React.
    window.location.href = cible;
  };

  const isDemo = Boolean(user?.is_demo);

  return (
    <AuthContext.Provider value={{ user, loading, isDemo, login, demoLogin, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
